import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export function Problem() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = [{
    src: 'https://cdn.magicpatterns.com/uploads/nrPjj4UC5RUcar8tszZ2ue/Image_1.png',
    alt: 'Progress transformation - before and after'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/1T6qRFmEW3SkHqsC3EiL4Y/Image_2.png',
    alt: 'Side profile transformation - before and after'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ay4UZQBB5ppY11y89aadN6/Image_3.png',
    alt: 'Full body transformation - before and after'
  }];
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % images.length);
  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + images.length) % images.length);
  return <section className="py-20 bg-white" data-id="element-132">
      <div className="max-w-3xl mx-auto px-4 sm:px-6" data-id="element-133">
        {/* The Quiet Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-20" data-id="element-134">
          <h2 className="font-serif text-4xl text-teal-900 mb-8 text-center" data-id="element-135">
            This might sound familiar…
          </h2>

          <div className="space-y-6 text-lg text-charcoal-900 leading-relaxed" data-id="element-136">
            <ul className="space-y-4 ml-6 list-none" data-id="element-137">
              <li className="flex items-start" data-id="element-138">
                <span className="text-rose-400 mr-3 text-xl" data-id="element-139">•</span>
                <span data-id="element-140">You used to lose weight easily — now nothing works.</span>
              </li>
              <li className="flex items-start" data-id="element-141">
                <span className="text-rose-400 mr-3 text-xl" data-id="element-142">•</span>
                <span data-id="element-143">You eat less… but your belly still grows.</span>
              </li>
              <li className="flex items-start" data-id="element-144">
                <span className="text-rose-400 mr-3 text-xl" data-id="element-145">•</span>
                <span data-id="element-146">The scale goes up and down for no reason.</span>
              </li>
              <li className="flex items-start" data-id="element-147">
                <span className="text-rose-400 mr-3 text-xl" data-id="element-148">•</span>
                <span data-id="element-149">You feel bloated, tired, or constantly hungry.</span>
              </li>
              <li className="flex items-start" data-id="element-150">
                <span className="text-rose-400 mr-3 text-xl" data-id="element-151">•</span>
                <span data-id="element-152">
                  You start strong… then life happens and everything falls
                  apart.
                </span>
              </li>
            </ul>
            <p data-id="element-153">
              <strong data-id="element-154">If this feels like you, you're not lazy.</strong>
              <br data-id="element-155" />
              <br data-id="element-156" />
              You're not broken.
              <br data-id="element-157" />
              <br data-id="element-158" />
              Your body has simply changed.
            </p>
            <p className="font-serif text-2xl text-teal-900 text-center py-6" data-id="element-159">
              And most weight-loss advice isn't designed for this stage of life.
            </p>
          </div>
        </motion.div>

        {/* Why Plans Fail */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-cream-100 p-8 md:p-12 rounded-lg border border-cream-200" data-id="element-160">
          <h3 className="font-serif text-3xl text-teal-900 mb-6" data-id="element-161">
            Why the old methods stop working after 35
          </h3>

          <p className="text-lg text-charcoal-900 mb-6" data-id="element-162">
            Most programs tell you to:
          </p>

          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-163">
            {['Eat less', 'Push harder', 'Do more cardio', 'Be more disciplined'].map((item, i) => <div key={i} className="bg-white p-4 rounded shadow-sm text-center font-medium text-charcoal-800" data-id="element-164">
                {item}
              </div>)}
          </div>

          <p className="text-lg text-charcoal-900 mb-6" data-id="element-165">
            But real life doesn't care about your meal plan.
          </p>

          <p className="text-lg text-charcoal-900" data-id="element-166">
            So what happens? You start strong… then the fatigue hits… cravings
            spike… sleep gets messy… you miss a few days… and suddenly you're
            "starting over" again.
          </p>
        </motion.div>

        {/* Image Carousel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-16" data-id="element-167">
          <div className="relative rounded-xl overflow-hidden shadow-lg border border-cream-200 bg-cream-50" data-id="element-168">
            <div className="relative aspect-[4/5] sm:aspect-[3/4] md:aspect-square overflow-hidden" data-id="element-169">
              <AnimatePresence mode="wait" data-id="element-170">
                <motion.img key={currentSlide} src={images[currentSlide].src} alt={images[currentSlide].alt} initial={{
                opacity: 0,
                x: 40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -40
              }} transition={{
                duration: 0.35,
                ease: 'easeInOut'
              }} className="w-full h-full object-cover absolute inset-0" data-id="element-171" />
              </AnimatePresence>
            </div>

            {/* Navigation arrows */}
            <button onClick={prevSlide} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors" aria-label="Previous image" data-id="element-172">
              <ChevronLeft className="w-5 h-5 text-charcoal-800" data-id="element-173" />
            </button>
            <button onClick={nextSlide} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors" aria-label="Next image" data-id="element-174">
              <ChevronRight className="w-5 h-5 text-charcoal-800" data-id="element-175" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-176">
            {images.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2.5 h-2.5 rounded-full transition-colors ${index === currentSlide ? 'bg-teal-900' : 'bg-charcoal-800/20 hover:bg-charcoal-800/40'}`} aria-label={`Go to image ${index + 1}`} data-id="element-177" />)}
          </div>
        </motion.div>
      </div>
    </section>;
}